import { LayoutDashboard, PlusCircle, Settings2 } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmDelete } from '@unistocks/components/delete-button/delete-button';
import { HeaderIcon } from '@unistocks/components/modal/header-icon';
import { Page } from '@unistocks/components/page/page';
import { FullScreenSheet } from '@unistocks/components/sheet/full-screen-sheet';
import { AddDashboardItemForm } from '@unistocks/features/dashboard/components/add-item/add-item';
import { DashboardGrid } from '@unistocks/features/dashboard/dashboard-grid';
import { renderSettingsForm } from '@unistocks/features/dashboard/render-dashboard-item';
import { hasMultipleLayouts } from '@unistocks/features/dashboard/widget-registry';
import { DashboardItemDto } from '@unistocks/proxy/axios-client';
import {
  dashboardGetQueryKey,
  useDashboardDeleteItemMutationWithParameters,
  useDashboardGetQuery
} from '@unistocks/proxy/axios-client/DashboardQuery';
import { t } from '@unistocks/shared/localization/index.ts';
import { isDefined } from '@unistocks/shared/utils/util';
import { useCallback, useMemo, useState } from 'react';
import { Spinner } from 'tamagui';

type SettingsState = {
  open: boolean;
  item?: DashboardItemDto;
  settingsTitle?: string;
};

export const Dashboard: React.FC = () => {
  const queryClient = useQueryClient();
  const [{ open, item, settingsTitle }, setSettings] = useState<SettingsState>({ open: false });

  const { data: dashboard, isSuccess } = useDashboardGetQuery();

  const openSettings = useCallback((item: DashboardItemDto, title?: string) => {
    setSettings({
      open: true,
      item,
      settingsTitle: isDefined(title) ? title : t(`::Enum:DashboardItemType:${item.type}`)
    });
  }, []);

  const openAddItem = useCallback(() => {
    setSettings({
      open: true,
      item: undefined,
      settingsTitle: t('::AddWhat', { 0: t('::Content').toLowerCase() })
    });
  }, []);

  const onOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setSettings({ open: false });
    }
  }, []);

  const SettingsForm = useMemo(() => {
    if (!open) return null;

    if (item) {
      return renderSettingsForm(item, () => onOpenChange(false));
    }

    return (
      <AddDashboardItemForm
        onSubmit={() => onOpenChange(false)}
        onCancel={() => onOpenChange(false)}
      />
    );
  }, [item, open, onOpenChange]);

  const { mutate: deleteItem, isPending: isPendingDelete } =
    useDashboardDeleteItemMutationWithParameters({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: dashboardGetQueryKey() });
        setSettings({ open: false, item: undefined, settingsTitle: undefined });
      }
    });

  const renderSheetHeaderRight = () => {
    return isPendingDelete ? (
      <Spinner size={'small'} color={'$unistocks'} />
    ) : (
      <ConfirmDelete
        message={t('::Dashboard:Delete:Confirm')}
        onConfirm={() => deleteItem({ id: item!.id })}
      />
    );
  };

  return (
    <Page
      size="fullscreen"
      queryState={{
        queryKeys: [dashboardGetQueryKey()],
        loadingText: t('::Dashboard:Loading'),
        isEmpty: isSuccess && !dashboard.items.length,
        noDataIcon: LayoutDashboard,
        noDataText: t('::Dashboard:NoData')
      }}
    >
      {isSuccess ? (
        <DashboardGrid
          settingsOpen={open && !!item}
          openSettings={openSettings}
          addItemOpen={open && !item}
          openAddItem={openAddItem}
        />
      ) : null}

      <FullScreenSheet
        open={open}
        onOpenChange={onOpenChange}
        headerIcon={
          <HeaderIcon
            Icon={item ? Settings2 : PlusCircle}
            color={item ? '$unistocks' : '$success'}
          />
        }
        headerTitle={settingsTitle}
        headerRight={item ? renderSheetHeaderRight() : null}
        headerProps={!item || hasMultipleLayouts(item.type) ? { mb: 0 } : undefined}
      >
        {/* Do not use Sortable.PortalProvider, it conflicts with BottomSheet portals */}
        {SettingsForm}
      </FullScreenSheet>
    </Page>
  );
};

Dashboard.displayName = 'Dashboard';
